import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { calendarAPI, ptOrdersAPI } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Phone, Mail, Calendar as CalendarIcon,
  Building2, MessageSquare, FileText, Clock, CheckCircle2, AlertTriangle, Plus, Dumbbell, CalendarClock, CalendarDays, Lock, Stethoscope
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import CheckupTrainerKarte from '@/components/CheckupTrainerKarte';
import AngebotsvorlagenKarte from '@/components/AngebotsvorlagenKarte';
import ErgebnisEinrichtungKarte from '@/components/checkup/ErgebnisEinrichtungKarte';
import { TrainerLinks, PtTerminAnlegen, PtTerminZeile, ExternUebertragen } from '@/components/TrainerVerwaltung';
import { CheckupZeile } from '@/components/CheckupKalender';
import EinsatzKalender from '@/components/EinsatzKalender';
import { KalenderAboDialog, zeitpunktDE } from '@/components/KalenderAbo';
import { monatsTage, bereichUm, tageVonBis, tagesAnteil } from '@/lib/kalenderTage';

const TYPE_CONFIG = {
  call: { icon: Phone, color: '#3B82F6', label: 'Anruf' },
  email: { icon: Mail, color: '#8B5CF6', label: 'E-Mail' },
  meeting: { icon: CalendarIcon, color: '#F59E0B', label: 'Termin' },
  visit: { icon: Building2, color: '#10B981', label: 'Besuch' },
  message: { icon: MessageSquare, color: '#EC4899', label: 'Nachricht' },
  note: { icon: FileText, color: '#6B7280', label: 'Notiz' },
  follow_up: { icon: Clock, color: '#F97316', label: 'Nachfassen' },
};

const STATUS_LABELS = {
  pending: 'Offen',
  completed: 'Erledigt',
  cancelled: 'Abgesagt',
};

// Montag-Start (DE Standard)
const DAYS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

function dateKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseEventDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
}

function formatDateDE(year, month, day) {
  return new Date(year, month, day).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatTimeDE(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [activities, setActivities] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [ptOrders, setPtOrders] = useState([]);
  const [beratungen, setBeratungen] = useState([]);
  const [ptTermine, setPtTermine] = useState([]);
  const [checkups, setCheckups] = useState([]);
  // Ansicht (15.10.2026): allgemeiner Kalender oder Einsatz-Kalender (Kurse, PT, Check-Ups) — je Gerät gemerkt
  const [ansicht, setAnsicht] = useState(() => { try { return localStorage.getItem('p1_kalender_ansicht') || 'allgemein'; } catch { return 'allgemein'; } });
  const ansichtSetzen = (a) => { setAnsicht(a); try { localStorage.setItem('p1_kalender_ansicht', a); } catch { /* egal */ } };
  // Kalender-Abo (05.10.2026): angebundene Kalender (z. B. OptiOffice des Inhabers), nur für Besitzer und Freigegebene
  const [externeTermine, setExterneTermine] = useState([]);
  const [externeKalender, setExterneKalender] = useState([]);
  const [aboOffen, setAboOffen] = useState(false);
  const [ladeFehler, setLadeFehler] = useState(false);
  const anfrageNr = useRef(0);
  const [openPtOrders, setOpenPtOrders] = useState([]);
  const [acceptOrder, setAcceptOrder] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { userPermissions, isAdminPermissions } = useAuth();
  const isTrainer = isAdminPermissions || !!userPermissions?.['app.trainer_access'];
  const darfPt = isAdminPermissions || !!userPermissions?.['leads.edit'];

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const start = new Date(year, month - 1, 1).toISOString();
    const end = new Date(year, month + 2, 0).toISOString();
    const anfrage = ++anfrageNr.current;
    try {
      const res = await calendarAPI.getEvents({ start, end });
      if (anfrage !== anfrageNr.current) return;       // beim schnellen Blättern überholt: nur die jüngste Antwort zählt
      setLadeFehler(false);
      setActivities(res.data.activities || []);
      setFollowUps(res.data.follow_ups || []);
      setPtOrders(res.data.pt_orders || []);
      setBeratungen(res.data.beratungen || []);
      setPtTermine(res.data.pt_termine || []);
      setCheckups(res.data.checkups || []);
      setExterneTermine(res.data.externe_termine || []);
      setExterneKalender(res.data.externe_kalender || []);
    } catch (err) {
      if (anfrage !== anfrageNr.current) return;
      // Der alte Bestand gehört zu einem anderen Zeitraum: lieber nichts zeigen als „frei“ vortäuschen
      setExterneTermine([]); setLadeFehler(true);
    }
    finally { if (anfrage === anfrageNr.current) setLoading(false); }
  }, [year, month]);

  const fetchOpenPtOrders = useCallback(async () => {
    if (!isTrainer) return;
    try {
      const res = await ptOrdersAPI.listOpen();
      setOpenPtOrders(res.data.pt_orders || []);
    } catch (err) { /* still loading permissions or not a trainer */ }
  }, [isTrainer]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchOpenPtOrders(); }, [fetchOpenPtOrders]);

  const openAcceptDialog = (order) => {
    setAcceptOrder(order);
    setScheduledDate('');
    setScheduledStart('');
    setScheduledEnd('');
  };

  const submitAccept = async () => {
    if (!acceptOrder || !scheduledDate || !scheduledStart || !scheduledEnd) {
      toast.error('Bitte Datum, Start- und Endzeit angeben');
      return;
    }
    setAccepting(true);
    try {
      await ptOrdersAPI.accept(acceptOrder.id, {
        scheduled_date: scheduledDate,
        scheduled_start: scheduledStart,
        scheduled_end: scheduledEnd,
      });
      toast.success('PT-Auftrag angenommen');
      setAcceptOrder(null);
      fetchOpenPtOrders();
      fetchEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Annehmen');
      if (err?.response?.status === 409) {
        setAcceptOrder(null);
        fetchOpenPtOrders();
      }
    } finally {
      setAccepting(false);
    }
  };

  // Event-Map nach Datum
  const eventMap = useMemo(() => {
    const map = {};
    const addToMap = (key, event) => {
      if (!map[key]) map[key] = [];
      map[key].push(event);
    };
    activities.forEach(a => {
      const d = parseEventDate(a.event_date || a.created_at);
      if (d) addToMap(dateKey(d.year, d.month, d.day), { ...a, kind: 'activity' });
    });
    followUps.forEach(f => {
      const d = parseEventDate(f.event_date || f.due_date);
      if (d) addToMap(dateKey(d.year, d.month, d.day), { ...f, kind: 'follow_up' });
    });
    ptOrders.forEach(p => {
      // scheduled_date ist bereits "YYYY-MM-DD" — gleiches Format wie dateKey(), kein Parsing nötig
      if (p.scheduled_date) addToMap(p.scheduled_date, { ...p, kind: 'pt_order' });
    });
    beratungen.forEach(b => {
      if (b.scheduled_date) addToMap(b.scheduled_date, { ...b, kind: 'beratung' });
    });
    ptTermine.forEach(t => {
      if (t.datum) addToMap(t.datum, { ...t, kind: 'pt_termin' });
    });
    checkups.forEach(c => {
      if (c.datum) addToMap(c.datum, { ...c, kind: 'checkup' });
    });
    // Termine aus angebundenen Kalendern stehen an jedem Tag, den sie berühren
    // derselbe Bereich, den fetchEvents anfragt: Vormonat bis Ende des Folgemonats
    const bereich = bereichUm(year, month);
    externeTermine.forEach((t, i) => {
      tageVonBis(t.tag_von, t.tag_bis, bereich.von, bereich.bis).forEach(tag => {
        const anteil = tagesAnteil(t, tag);        // an diesem Tag: „ab 14:00“, „ganztägig“ oder „bis 12:00“
        addToMap(tag, { ...t, id: `ext-${i}-${tag}`, kind: 'extern', mehrtaegig: t.tag_bis > t.tag_von, zeitText: anteil.text, sortierung: anteil.sortierung });
      });
    });
    return map;
  }, [activities, followUps, ptOrders, beratungen, ptTermine, checkups, externeTermine, year, month]);

  const monthDays = monatsTage(year, month);
  const today = new Date();
  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());

  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();
  const selectedKey = selectedDate
    ? dateKey(selectedDate.year, selectedDate.month, selectedDate.day)
    : (isCurrentMonth ? todayKey : null);
  const selectedEvents = selectedKey ? (eventMap[selectedKey] || []) : [];
  const selectedFollowUps = useMemo(() => selectedEvents.filter(e => e.kind === 'follow_up'), [selectedEvents]);
  const selectedActivities = useMemo(() => selectedEvents.filter(e => e.kind === 'activity'), [selectedEvents]);
  const selectedPtOrders = useMemo(() => selectedEvents.filter(e => e.kind === 'pt_order'), [selectedEvents]);
  const selectedBeratungen = useMemo(() => selectedEvents.filter(e => e.kind === 'beratung'), [selectedEvents]);
  const selectedPtTermine = useMemo(() => selectedEvents.filter(e => e.kind === 'pt_termin').sort((a, b) => (a.start || '').localeCompare(b.start || '')), [selectedEvents]);
  const selectedCheckups = useMemo(() => selectedEvents.filter(e => e.kind === 'checkup').sort((a, b) => (a.start || a.vorschlag || '').localeCompare(b.start || b.vorschlag || '')), [selectedEvents]);
  const offeneAnfragen = useMemo(() => checkups.filter(c => c.status === 'offen').sort((a, b) => a.datum.localeCompare(b.datum)), [checkups]);
  const selectedExterne = useMemo(() => selectedEvents.filter(e => e.kind === 'extern').sort((a, b) => (a.sortierung || '').localeCompare(b.sortierung || '')), [selectedEvents]);
  const kalenderName = (id) => externeKalender.find(k => k.id === id)?.bezeichnung || 'Kalender';
  // Der angezeigte Monat liegt ganz außerhalb dessen, was vom fremden Kalender geladen ist (weit zurück oder voraus)
  const ausserhalbGeladen = (k) => {
    const erster = dateKey(year, month, 1);
    const letzter = dateKey(year, month, new Date(year, month + 1, 0).getDate());
    return !!k.stand && ((k.geladen_bis && erster > k.geladen_bis) || (k.geladen_ab && letzter < k.geladen_ab));
  };
  const externKurz = externeKalender.length === 1 ? externeKalender[0].bezeichnung : 'Kalender';

  // Monats-Statistik
  const monthEvents = useMemo(() => {
    let fus = 0, acts = 0, overdue = 0, pts = 0, bers = 0;
    Object.entries(eventMap).forEach(([, events]) => {
      events.forEach(e => {
        if (e.kind === 'follow_up') { fus++; if (e.status === 'pending' && new Date(e.due_date) < new Date()) overdue++; }
        if (e.kind === 'activity') acts++;
        if (e.kind === 'pt_order') pts++;
        if (e.kind === 'beratung') bers++;
      });
    });
    return { fus, acts, overdue, pts, bers };
  }, [eventMap]);

  const prevMonth = () => { setCurrentDate(new Date(year, month - 1, 1)); setSelectedDate(null); };
  const nextMonth = () => { setCurrentDate(new Date(year, month + 1, 1)); setSelectedDate(null); };
  const goToday = () => { setCurrentDate(new Date()); setSelectedDate(null); };

  return (
    <div className="space-y-4 fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="calendar-title">Kalender</h1>
          <p className="text-muted-foreground text-sm">
            {monthEvents.fus} Nachfassaktionen · {monthEvents.acts} Aktivitäten
            {isTrainer && monthEvents.pts > 0 && <> · {monthEvents.pts} PT-Termine</>}
            {monthEvents.bers > 0 && <> · {monthEvents.bers} Erstberatungen</>}
            {offeneAnfragen.length > 0 && <span className="text-amber-700 dark:text-amber-400 font-medium"> · {offeneAnfragen.length} Check-Up-Anfrage{offeneAnfragen.length === 1 ? '' : 'n'} offen</span>}
            {monthEvents.overdue > 0 && <span className="text-destructive font-medium"> · {monthEvents.overdue} überfällig</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdminPermissions && (
            <Button variant="outline" size="sm" onClick={() => setAboOffen(true)} className="text-xs h-8 gap-1" data-testid="cal-abo-btn">
              <CalendarDays className="w-3.5 h-3.5" /> Mein Kalender
            </Button>
          )}
          {ansicht === 'allgemein' && <>
          <Button variant="outline" size="sm" onClick={goToday} className="text-xs h-8" data-testid="cal-today-btn">Heute</Button>
          <div className="flex items-center gap-1 bg-card border border-border rounded-lg">
            <Button variant="ghost" size="sm" onClick={prevMonth} className="h-8 w-8 p-0 rounded-l-lg" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-semibold w-40 text-center" data-testid="cal-month-label">{MONTHS[month]} {year}</span>
            <Button variant="ghost" size="sm" onClick={nextMonth} className="h-8 w-8 p-0 rounded-r-lg" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          </>}
        </div>
      </div>

      {/* Schieberegler: Allgemein | Einsätze & Check-Ups */}
      <div className="relative inline-grid grid-cols-2 rounded-full border border-border bg-muted/40 p-1 text-xs font-medium select-none" data-testid="kalender-ansicht">
        <span aria-hidden className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-full bg-card shadow-sm border border-border/60 transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{ transform: ansicht === 'einsatz' ? 'translateX(100%)' : 'translateX(0)' }} />
        {[['allgemein', 'Allgemein'], ['einsatz', <>Einsätze<span className="hidden sm:inline"> & Check-Ups</span></>]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => ansichtSetzen(k)} data-testid={`kalender-ansicht-${k}`}
            className={`relative z-10 px-4 py-1.5 rounded-full whitespace-nowrap transition-colors ${ansicht === k ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {l}{k === 'einsatz' && offeneAnfragen.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px]">{offeneAnfragen.length}</span>}
          </button>
        ))}
      </div>

      {ansicht === 'einsatz' ? <EinsatzKalender darfBearbeiten={darfPt} /> : (<>
      {ladeFehler && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs" data-testid="cal-ladefehler">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Der Kalender konnte nicht geladen werden. Was hier steht, kann unvollständig sein.
          <Button variant="outline" size="sm" className="h-6 text-[11px] ml-auto" onClick={fetchEvents}>Neu laden</Button>
        </div>
      )}

      {/* Angebundene Kalender: wessen Termine hier zusätzlich stehen und wie alt der Stand ist */}
      {externeKalender.map(k => (
        <div key={k.id} data-testid={`cal-extern-${k.id}`}
             className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border px-3 py-2 text-xs ${k.veraltet || k.fehler ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30' : 'border-indigo-200 dark:border-indigo-900/40 bg-indigo-50/40 dark:bg-indigo-900/10'}`}>
          <Lock className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
          <span className="font-medium">{k.bezeichnung}</span>
          <span className="text-muted-foreground">
            {k.eigener ? 'dein angebundener Kalender' : 'für dich freigegeben, nur lesend'}{!k.mit_titel ? ', ohne Titel' : ''}
            {k.stand ? ` · Stand ${zeitpunktDE(k.stand)}` : ' · noch nicht abgerufen'}
            {k.stand && k.anzahl === 0 ? ' · keine Termine im Kalender' : ''}
          </span>
          {(k.veraltet || k.fehler) && <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> Stand ist alt, bitte nicht darauf verlassen</span>}
          {k.unvollstaendig && <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400 font-medium" data-testid="cal-extern-unvollstaendig"><AlertTriangle className="w-3.5 h-3.5" /> Kalender unvollständig gelesen, es können Termine fehlen</span>}
          {ausserhalbGeladen(k) && <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> Für diesen Monat sind keine Termine geladen{k.geladen_bis ? ` (geladen bis ${k.geladen_bis.split('-').reverse().join('.')})` : ''}</span>}
        </div>
      ))}

      {/* Check-Up-Anfragen aus der App (15.10.2026): warten auf Freigabe — ein Klick springt zum Wunschtag */}
      {offeneAnfragen.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10 px-3 py-2 text-xs" data-testid="cal-checkup-offen">
          <Stethoscope className="w-3.5 h-3.5 text-amber-600" />
          <span className="font-medium">Check-Up-Anfragen warten auf Freigabe:</span>
          {offeneAnfragen.map(c => {
            const [y, m, d] = c.datum.split('-').map(Number);
            return (
              <button key={c.id} type="button" onClick={() => setSelectedDate({ year: y, month: m - 1, day: d })}
                className="rounded-full border border-amber-300 dark:border-amber-800 bg-card px-2 py-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/30">
                {c.profil_name} · {d}.{m}. {c.zeitfenster_text || ''}
              </button>
            );
          })}
        </div>
      )}

      {/* Trainer-Links (Cagla): Kalender per Link fuer jeden Trainer */}
      {darfPt && <TrainerLinks onChange={fetchEvents} />}
      {darfPt && <CheckupTrainerKarte />}
      {darfPt && <AngebotsvorlagenKarte />}
      {darfPt && <ErgebnisEinrichtungKarte />}

      {/* Offene PT-Aufträge — nur für Trainer sichtbar */}
      {isTrainer && openPtOrders.length > 0 && (
        <Card className="border-purple-300 bg-purple-50/50 dark:bg-purple-900/10" data-testid="pt-open-orders-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Dumbbell className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <h3 className="text-sm font-semibold">
                {openPtOrders.length} offene{openPtOrders.length === 1 ? 'r' : ''} PT-Auftrag{openPtOrders.length === 1 ? '' : 'e'}
              </h3>
            </div>
            <div className="space-y-2">
              {openPtOrders.map(o => (
                <div key={o.id} className="flex items-center justify-between gap-3 bg-card border border-border/60 rounded-lg px-3 py-2" data-testid={`pt-open-${o.id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{o.member_name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{o.product_name}</p>
                  </div>
                  <Button size="sm" className="h-7 text-xs flex-shrink-0" onClick={() => openAcceptDialog(o)} data-testid={`pt-accept-btn-${o.id}`}>
                    Annehmen
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Kalender-Grid */}
        <div className="flex-1">
          {/* Desktop Grid */}
          <div className="hidden sm:block">
            <Card className="border-border/60 overflow-hidden">
              <div className="grid grid-cols-7">
                {DAYS_SHORT.map(d => (
                  <div key={d} className="py-2 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border bg-muted/30">{d}</div>
                ))}
                {monthDays.map((d) => {
                  const key = dateKey(d.year, d.month, d.day);
                  const events = eventMap[key] || [];
                  const isToday = key === todayKey;
                  const isSelected = key === selectedKey;
                  const fuCount = events.filter(e => e.kind === 'follow_up').length;
                  const actCount = events.filter(e => e.kind === 'activity').length;
                  const ptCount = events.filter(e => e.kind === 'pt_order').length + events.filter(e => e.kind === 'pt_termin').length;
                  const berCount = events.filter(e => e.kind === 'beratung').length;
                  const extCount = events.filter(e => e.kind === 'extern').length;
                  const cuOffen = events.filter(e => e.kind === 'checkup' && e.status === 'offen').length;
                  const cuFest = events.filter(e => e.kind === 'checkup' && e.status !== 'offen').length;
                  const hasOverdue = events.some(e => e.kind === 'follow_up' && e.status === 'pending' && new Date(e.due_date) < new Date());

                  return (
                    <button
                      key={`${d.year}-${d.month}-${d.day}`}
                      onClick={() => setSelectedDate({ year: d.year, month: d.month, day: d.day })}
                      data-testid={`cal-day-${key}`}
                      className={`relative min-h-[80px] p-1.5 border-b border-r border-border/40 text-left transition-colors hover:bg-muted/30 ${
                        !d.isCurrentMonth ? 'bg-muted/10' : ''
                      } ${isSelected ? 'bg-primary/5 ring-1 ring-primary/30' : ''}`}
                    >
                      <span className={`text-xs font-medium inline-flex items-center justify-center ${
                        !d.isCurrentMonth ? 'text-muted-foreground/40' :
                        isToday ? 'text-white bg-marke rounded-full w-6 h-6' :
                        'text-foreground'
                      }`}>
                        {d.day}
                      </span>
                      {events.length > 0 && d.isCurrentMonth && (
                        <div className="mt-1 space-y-0.5">
                          {fuCount > 0 && (
                            <div className={`flex items-center gap-1 text-[9px] rounded px-1 py-0.5 ${hasOverdue ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'}`}>
                              <Clock className="w-2.5 h-2.5 flex-shrink-0" />{fuCount} {fuCount === 1 ? 'Nachfassen' : 'Nachfassen'}
                            </div>
                          )}
                          {actCount > 0 && (
                            <div className="flex items-center gap-1 text-[9px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded px-1 py-0.5">
                              <CalendarIcon className="w-2.5 h-2.5 flex-shrink-0" />{actCount} {actCount === 1 ? 'Aktivität' : 'Aktivitäten'}
                            </div>
                          )}
                          {ptCount > 0 && (
                            <div className="flex items-center gap-1 text-[9px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded px-1 py-0.5">
                              <Dumbbell className="w-2.5 h-2.5 flex-shrink-0" />{ptCount} PT
                            </div>
                          )}
                          {cuOffen > 0 && (
                            <div className="flex items-center gap-1 text-[9px] bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded px-1 py-0.5 border border-dashed border-amber-400">
                              <Stethoscope className="w-2.5 h-2.5 flex-shrink-0" />{cuOffen} Anfrage
                            </div>
                          )}
                          {cuFest > 0 && (
                            <div className="flex items-center gap-1 text-[9px] bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded px-1 py-0.5">
                              <Stethoscope className="w-2.5 h-2.5 flex-shrink-0" />{cuFest} Check-Up
                            </div>
                          )}
                          {berCount > 0 && (
                            <div className="flex items-center gap-1 text-[9px] bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded px-1 py-0.5">
                              <CalendarClock className="w-2.5 h-2.5 flex-shrink-0" />{berCount} Beratung
                            </div>
                          )}
                          {extCount > 0 && (
                            <div className="flex items-center gap-1 text-[9px] bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded px-1 py-0.5">
                              <Lock className="w-2.5 h-2.5 flex-shrink-0" /><span className="truncate" title={externKurz}>{extCount} belegt</span>
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Mobile: Wochenansicht */}
          <div className="sm:hidden">
            <MobileWeekView
              currentDate={currentDate}
              selectedKey={selectedKey}
              todayKey={todayKey}
              eventMap={eventMap}
              onSelectDate={(d) => setSelectedDate(d)}
              onPrevWeek={() => setCurrentDate(new Date(currentDate.getTime() - 7 * 86400000))}
              onNextWeek={() => setCurrentDate(new Date(currentDate.getTime() + 7 * 86400000))}
            />
          </div>
        </div>

        {/* Tagesdetail-Panel */}
        <div className="lg:w-[340px]">
          <Card className="border-border/60 sticky top-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold" data-testid="cal-day-title">
                  {selectedDate
                    ? formatDateDE(selectedDate.year, selectedDate.month, selectedDate.day)
                    : isCurrentMonth
                    ? 'Heute'
                    : 'Datum wählen'
                  }
                </h3>
                <Badge variant="secondary" className="text-[10px]">
                  {selectedEvents.length} {selectedEvents.length === 1 ? 'Termin' : 'Termine'}
                </Badge>
              </div>

              {loading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={`cal-skel-${i}`} className="h-16 bg-muted animate-pulse rounded-lg" />
                  ))}
                </div>
              ) : selectedEvents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="cal-no-events">
                  <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-20" />
                  <p className="text-xs">
                    {!selectedKey ? 'Klicke auf ein Datum' : 'Keine Termine an diesem Tag'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[450px] overflow-y-auto">
                  {/* Angebundene Kalender zuerst: wer hier Termine legt, will wissen, wann der Inhaber belegt ist */}
                  {selectedExterne.length > 0 && (
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-1">{externKurz}</p>
                  )}
                  {selectedExterne.map((ev) => (
                    <div key={ev.id} className="p-2.5 rounded-lg border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50/40 dark:bg-indigo-900/10" data-testid="cal-extern-event">
                      <div className="flex items-start gap-2.5">
                        <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-indigo-100 dark:bg-indigo-900/30">
                          <Lock className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium break-words">{ev.titel}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {ev.zeitText}{ev.mehrtaegig ? ' · mehrtägig' : ''}
                            {externeKalender.length > 1 ? ` · ${kalenderName(ev.abo_id)}` : ''}
                          </p>
                          {ev.uid !== undefined && <ExternUebertragen ev={ev} onGespeichert={fetchEvents} />}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Follow-ups zuerst */}
                  {selectedFollowUps.length > 0 && (
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-1">Nachfassaktionen</p>
                  )}
                  {selectedFollowUps.map((ev) => {
                    const isOverdue = ev.status === 'pending' && new Date(ev.due_date) < new Date();
                    const typeCfg = TYPE_CONFIG[ev.type] || TYPE_CONFIG.follow_up;
                    return (
                      <button
                        key={ev.id}
                        onClick={() => navigate(`/leads/${ev.lead_id}`)}
                        className={`w-full text-left p-2.5 rounded-lg border transition-colors group ${
                          isOverdue ? 'border-red-300 bg-destructive/5 hover:bg-destructive/10' :
                          ev.status === 'completed' ? 'border-green-200 bg-green-50/30 hover:bg-green-50/50 dark:bg-green-900/10' :
                          'border-border/40 hover:bg-muted/30'
                        }`}
                        data-testid={`cal-event-${ev.id}`}
                      >
                        <div className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                            isOverdue ? 'bg-red-100 dark:bg-red-900/30' :
                            ev.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30' :
                            'bg-orange-100 dark:bg-orange-900/30'
                          }`}>
                            {ev.status === 'completed'
                              ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                              : isOverdue
                              ? <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                              : <Clock className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
                            }
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium">{typeCfg.label}</span>
                              <Badge variant={isOverdue ? 'destructive' : ev.status === 'completed' ? 'default' : 'secondary'} className="text-[8px] h-4">
                                {isOverdue ? 'Überfällig' : STATUS_LABELS[ev.status] || ev.status}
                              </Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">{ev.lead_name}</p>
                            {ev.notes && <p className="text-[10px] text-muted-foreground/70 mt-0.5 line-clamp-1">{ev.notes}</p>}
                            <p className="text-[9px] text-muted-foreground/50 mt-1">{formatTimeDE(ev.due_date)} Uhr</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {/* Aktivitäten */}
                  {selectedActivities.length > 0 && (
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-3">Aktivitäten</p>
                  )}
                  {selectedActivities.map((ev) => {
                    const cfg = TYPE_CONFIG[ev.type] || TYPE_CONFIG.note;
                    const Icon = cfg.icon;
                    return (
                      <button
                        key={ev.id}
                        onClick={() => navigate(`/leads/${ev.lead_id}`)}
                        className="w-full text-left p-2.5 rounded-lg border border-border/40 hover:bg-muted/30 transition-colors"
                        data-testid={`cal-event-${ev.id}`}
                      >
                        <div className="flex items-start gap-2.5">
                          <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                            style={{ background: `color-mix(in srgb, ${cfg.color} 15%, transparent)` }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium">{cfg.label}</span>
                              {ev.outcome && <Badge variant="secondary" className="text-[8px] h-4">{ev.outcome?.replace(/_/g, ' ')}</Badge>}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">{ev.lead_name}</p>
                            {ev.notes && <p className="text-[10px] text-muted-foreground/70 mt-0.5 line-clamp-1">{ev.notes}</p>}
                            <p className="text-[9px] text-muted-foreground/50 mt-1">{formatTimeDE(ev.created_at)} Uhr</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {/* Personal Training der Trainer-Links (18.09.2026) */}
                  {(selectedPtTermine.length > 0 || darfPt) && (
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Personal Training</p>
                    </div>
                  )}
                  {selectedPtTermine.map((t) => <PtTerminZeile key={t.id} t={t} darfBearbeiten={darfPt} onChange={fetchEvents} />)}
                  {darfPt && selectedKey && <PtTerminAnlegen datum={selectedKey} onGespeichert={fetchEvents} />}

                  {/* Check-Ups: Anfragen aus der App und freigegebene Termine (15.10.2026) */}
                  {selectedCheckups.length > 0 && (
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-3">Check-Ups</p>
                  )}
                  {selectedCheckups.map((c) => <CheckupZeile key={c.id} c={c} darfBearbeiten={darfPt} onChange={fetchEvents} />)}

                  {/* PT-Aufträge */}
                  {selectedPtOrders.length > 0 && (
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-3">PT-Termine</p>
                  )}
                  {selectedPtOrders.map((ev) => (
                    <button
                      key={ev.id}
                      onClick={() => navigate(`/leads/${ev.member_id}`)}
                      className="w-full text-left p-2.5 rounded-lg border border-purple-200 dark:border-purple-900/40 bg-purple-50/30 dark:bg-purple-900/10 hover:bg-purple-50/60 transition-colors"
                      data-testid={`cal-pt-event-${ev.id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-purple-100 dark:bg-purple-900/30">
                          <Dumbbell className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium">{ev.product_name}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">{ev.member_name} · {ev.trainer_name}</p>
                          <p className="text-[9px] text-muted-foreground/50 mt-1">{ev.scheduled_start}–{ev.scheduled_end} Uhr</p>
                        </div>
                      </div>
                    </button>
                  ))}

                  {/* Erstberatungen (öffentliche Buchung) */}
                  {selectedBeratungen.length > 0 && (
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-3">Erstberatungen</p>
                  )}
                  {selectedBeratungen.map((ev) => (
                    <button
                      key={ev.id}
                      onClick={() => ev.lead_id && navigate(`/leads/${ev.lead_id}`)}
                      className="w-full text-left p-2.5 rounded-lg border border-teal-200 dark:border-teal-900/40 bg-teal-50/30 dark:bg-teal-900/10 hover:bg-teal-50/60 transition-colors"
                      data-testid={`cal-beratung-event-${ev.id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-teal-100 dark:bg-teal-900/30">
                          <CalendarClock className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium">Erstberatung</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">{ev.name}{ev.staff_name ? ` · ${ev.staff_name}` : ''}</p>
                          {ev.anliegen && <p className="text-[10px] text-muted-foreground/70 mt-0.5 line-clamp-1">{ev.anliegen}</p>}
                          <p className="text-[9px] text-muted-foreground/50 mt-1">{ev.scheduled_start}–{ev.scheduled_end} Uhr</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Legende */}
      <div className="flex flex-wrap items-center gap-4 text-[10px] text-muted-foreground pt-1 border-t border-border">
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-orange-400" />Nachfassen</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-red-500" />Überfällig</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-blue-500" />Aktivität</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-green-500" />Erledigt</div>
        {isTrainer && <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-purple-500" />PT-Termin</div>}
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-teal-500" />Erstberatung</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm border border-dashed border-amber-500 bg-amber-200" />Check-Up-Anfrage</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Check-Up</div>
        {externeKalender.length > 0 && <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-indigo-500" />{externKurz}</div>}
      </div>

      </>)}

      {isAdminPermissions && <KalenderAboDialog offen={aboOffen} onClose={() => setAboOffen(false)} onGeaendert={fetchEvents} />}

      {/* PT-Auftrag annehmen */}
      <Dialog open={!!acceptOrder} onOpenChange={(v) => { if (!v) setAcceptOrder(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>PT-Auftrag annehmen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-muted/50 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Mitglied:</span>
                <span className="font-medium">{acceptOrder?.member_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Leistung:</span>
                <span className="font-medium">{acceptOrder?.product_name}</span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Datum</label>
              <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} data-testid="pt-accept-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Start</label>
                <Input type="time" value={scheduledStart} onChange={(e) => setScheduledStart(e.target.value)} data-testid="pt-accept-start" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Ende</label>
                <Input type="time" value={scheduledEnd} onChange={(e) => setScheduledEnd(e.target.value)} data-testid="pt-accept-end" />
              </div>
            </div>
            <Button className="w-full" onClick={submitAccept} disabled={accepting} data-testid="pt-accept-submit">
              {accepting ? 'Wird angenommen...' : 'Termin festlegen & annehmen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ─── Mobile Wochenansicht ─── */
function MobileWeekView({ currentDate, selectedKey, todayKey, eventMap, onSelectDate, onPrevWeek, onNextWeek }) {
  // Woche startet Montag
  const startOfWeek = new Date(currentDate);
  const dayOfWeek = startOfWeek.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  startOfWeek.setDate(startOfWeek.getDate() + mondayOffset);

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(d.getDate() + i);
    weekDays.push(d);
  }

  return (
    <div className="space-y-3" data-testid="cal-mobile-week">
      {/* Wochen-Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onPrevWeek} className="h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-xs font-semibold text-muted-foreground">
          {weekDays[0].toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })} — {weekDays[6].toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })}
        </span>
        <Button variant="ghost" size="sm" onClick={onNextWeek} className="h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Tages-Leiste */}
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((d, i) => {
          const key = dateKey(d.getFullYear(), d.getMonth(), d.getDate());
          const events = eventMap[key] || [];
          const isToday = key === todayKey;
          const isSelected = key === selectedKey;

          return (
            <button
              key={key}
              onClick={() => onSelectDate({ year: d.getFullYear(), month: d.getMonth(), day: d.getDate() })}
              className={`flex flex-col items-center py-2 rounded-lg transition-colors ${
                isSelected ? 'bg-primary text-primary-foreground' :
                isToday ? 'bg-primary/10' :
                'hover:bg-muted'
              }`}
              data-testid={`cal-mobile-day-${key}`}
            >
              <span className="text-[9px] uppercase font-medium opacity-60">{DAYS_SHORT[i]}</span>
              <span className="text-sm font-semibold mt-0.5">{d.getDate()}</span>
              {events.length > 0 && (
                <div className="flex gap-0.5 mt-1">
                  {events.some(e => e.kind === 'follow_up') && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-orange-400'}`} />}
                  {events.some(e => e.kind === 'activity') && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-blue-400'}`} />}
                  {events.some(e => e.kind === 'beratung') && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-teal-400'}`} />}
                  {events.some(e => e.kind === 'checkup') && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : events.some(e => e.kind === 'checkup' && e.status === 'offen') ? 'bg-amber-400' : 'bg-emerald-500'}`} />}
                  {events.some(e => e.kind === 'extern') && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-indigo-400'}`} />}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
